import { useEffect, useRef, useState } from 'react'
import type { PuzzleRendererProps } from '@/game/PuzzleRenderer'

type Config = { instructions?: string; cameraFacing?: 'environment' | 'user' }

/**
 * Camera/file picker with preview and upload progress. The image is downscaled
 * client-side for bandwidth; the server re-processes it and decides completion.
 */
export function PhotoPuzzle({ puzzle, onUploadPhoto, disabled }: PuzzleRendererProps) {
  const config = puzzle.publicConfig as Config
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  function reset() {
    setFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  async function submit() {
    if (!file) return
    setUploading(true)
    setProgress(0)
    try {
      await onUploadPhoto(file, setProgress)
    } finally {
      setUploading(false)
    }
  }

  const busy = disabled || uploading

  return (
    <div className="puzzle-form">
      {config.instructions && <p>{config.instructions}</p>}
      <input
        ref={inputRef}
        id={`photo-${puzzle.id}`}
        type="file"
        accept="image/*"
        capture={config.cameraFacing}
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        disabled={busy}
        hidden
      />
      {!file ? (
        <label htmlFor={`photo-${puzzle.id}`} className="button primary file-button">
          Take or choose a photo
        </label>
      ) : (
        <>
          {previewUrl && <img src={previewUrl} alt="Your photo preview" className="photo-preview" />}
          <p className="muted">
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
          </p>
          {uploading && (
            <progress className="upload-progress" value={progress} max={1} aria-label="Upload progress">
              {Math.round(progress * 100)}%
            </progress>
          )}
          <div className="button-row">
            <button type="button" onClick={reset} disabled={busy}>
              Retake
            </button>
            <button type="button" className="primary" onClick={() => void submit()} disabled={busy}>
              {uploading ? (progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : 'Processing…') : 'Submit photo'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
